import { Select } from '@mantine/core';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { useFitColumns } from '../../fit-columns.js';
import { formatDateTime } from '../../format.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { TablePager } from '../purchasing/table.js';
import classes from './PmsLibrary.module.css';
import type { ApplicationStatus, LibraryApplication, LibraryVessel } from './types.js';
import { PAGE_SIZE, pageOf, pagesOf } from './view.js';

export const APPLICATION_TONE: Record<ApplicationStatus, StatusTone> = {
  pending: 'warning',
  applied: 'success',
  rejected: 'danger',
};

/** Vessel Applications (ADR 0063 §7): talepler ve gemideki sonuçları. */
export function ApplicationsTab({
  applications,
  vessels,
}: {
  applications: LibraryApplication[];
  vessels: LibraryVessel[];
}) {
  const { t } = useTranslation();
  const [vesselId, setVesselId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const fit = useFitColumns<HTMLElement>();
  const rows = useMemo(
    () => applications.filter((a) => vesselId === null || a.vesselId === vesselId),
    [applications, vesselId],
  );
  const pages = pagesOf(rows.length);
  const current = Math.min(page, pages);
  const shown = pageOf(rows, current);
  const k = (key: string) => t(`pmsLibrary.columns.${key}`);
  return (
    <Panel title={t('pmsLibrary.tabs.applications')} flush>
      <div className={classes.toolbar}>
        <Select
          className={classes.filter}
          label={t('pmsLibrary.filters.vessel')}
          placeholder={t('pmsLibrary.filters.allVessels')}
          clearable
          data={vessels.map((v) => ({ value: v.id, label: v.name }))}
          value={vesselId}
          onChange={(value) => {
            setVesselId(value);
            setPage(1);
          }}
        />
      </div>
      {shown.length === 0 ? (
        <EmptyState title={t('pmsLibrary.empty.applications')} />
      ) : (
        <section
          ref={fit}
          className={table.tableWrap}
          aria-label={t('pmsLibrary.applicationsList')}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: kayan tablo klavyeyle kaydırılır
          tabIndex={0}
        >
          <table className={table.table}>
            <thead>
              <tr>
                <th scope="col" className={classes.colStatus}>
                  {k('date')}
                </th>
                <th scope="col">{k('vessel')}</th>
                <th scope="col" data-drop={2}>
                  {k('sentBy')}
                </th>
                <th scope="col" className={classes.colNarrow} data-numeric>
                  {k('item')}
                </th>
                <th scope="col" className={classes.colNarrow} data-numeric>
                  {k('jobs')}
                </th>
                <th scope="col" className={classes.colStatus}>
                  {k('status')}
                </th>
                <th scope="col" data-drop={3}>
                  {k('decided')}
                </th>
                <th scope="col" data-drop={1}>
                  {k('note')}
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((a) => (
                <tr key={a.id}>
                  <td>{formatDateTime(a.createdAt)}</td>
                  <td>{a.vesselName ?? '—'}</td>
                  <td data-drop={2}>{a.createdByName ?? '—'}</td>
                  <td data-numeric>{a.itemCount}</td>
                  <td data-numeric>{a.jobCount}</td>
                  <td>
                    <StatusBadge tone={APPLICATION_TONE[a.status]}>
                      {t(`pmsLibrary.applicationStatuses.${a.status}`)}
                    </StatusBadge>
                  </td>
                  <td data-drop={3}>
                    {a.decidedAt
                      ? `${a.decidedByName ?? '—'} · ${formatDateTime(a.decidedAt)}`
                      : '—'}
                  </td>
                  <td data-drop={1} title={a.reason ?? a.note ?? ''}>
                    {a.reason ?? a.note ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
      <TablePager
        page={current}
        pages={pages}
        summary={
          rows.length === 0
            ? ''
            : t('pmsLibrary.showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(rows.length, current * PAGE_SIZE),
                total: rows.length,
              })
        }
        label={t('pmsLibrary.tabs.applications')}
        onPage={setPage}
      />
    </Panel>
  );
}
